import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { api, timeAgo, formatEth, formatNumber } from "@/lib/api";
import { StatusBadge, MethodPill } from "@/components/StatusBadge";
import { Send, Copy, Check, ChevronDown, ChevronUp, Flame } from "lucide-react";

const CopyBtn = ({ text }) => {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="ml-2 inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]" data-testid="copy-btn">
      {ok ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

const DetailRow = ({ label, children, testId }) => (
  <div className="ks-detail-row" data-testid={testId}>
    <div className="ks-detail-label">{label}</div>
    <div className="ks-detail-value">{children}</div>
  </div>
);

const SectionDivider = ({ label }) => (
  <div className="px-4 py-2 bg-[var(--ks-surface)] border-t border-b border-[var(--ks-border)] text-xs uppercase tracking-wide text-[var(--ks-text-soft)] font-semibold">
    {label}
  </div>
);

export default function TxDetail() {
  const { hash } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    api.get(`/transactions/${hash}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [hash]);

  if (notFound) return <Layout><p className="text-lg" data-testid="tx-notfound">Transaction not found</p></Layout>;
  if (!data) return <Layout><p>Loading…</p></Layout>;

  const { transaction: t, confirmations } = data;

  // Derived EIP-1559-style values (our simulated chain uses a flat effective gas price).
  // We split it into base_fee (~80%) and priority_fee (~20%) for display parity with Etherscan.
  const effectiveGwei = Number(t.gas_price_gwei || 0);
  const baseFeeGwei = +(effectiveGwei * 0.8).toFixed(4);
  const priorityGwei = +(effectiveGwei - baseFeeGwei).toFixed(4);
  const maxFeeGwei = +(effectiveGwei * 1.15).toFixed(4);
  // Burnt = base_fee * gas_used (converted to ETH)
  const burntEth = (baseFeeGwei * 1e-9) * Number(t.gas_used || 0);
  const savingsEth = Math.max(0, ((maxFeeGwei - effectiveGwei) * 1e-9) * Number(t.gas_used || 0));
  const gasUsedPct = t.gas ? ((t.gas_used / t.gas) * 100).toFixed(2) : "0";

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <Send className="text-[var(--ks-primary)]" />
        <h1 className="text-xl font-semibold" data-testid="tx-title">Transaction Details</h1>
      </div>

      <div className="ks-card">
        <div className="ks-card-header"><span>Overview</span></div>

        <DetailRow label="Transaction Hash" testId="tx-hash">
          <span className="mono break-all">{t.hash}</span>
          <CopyBtn text={t.hash} />
        </DetailRow>
        <DetailRow label="Status" testId="tx-status"><StatusBadge status={t.status} /></DetailRow>
        <DetailRow label="Block" testId="tx-block">
          <Link to={`/block/${t.block_number}`} className="ks-link">{formatNumber(t.block_number)}</Link>{" "}
          <span className="ks-badge ml-2">{confirmations} Block Confirmations</span>
        </DetailRow>
        <DetailRow label="Timestamp" testId="tx-timestamp">
          {timeAgo(t.timestamp)} · {new Date(t.timestamp * 1000).toUTCString()}
        </DetailRow>

        <SectionDivider label="From / To" />

        <DetailRow label="From" testId="tx-from">
          <Link to={`/address/${t.from_address}`} className="ks-link mono break-all">{t.from_address}</Link>
          <CopyBtn text={t.from_address} />
        </DetailRow>
        <DetailRow label="To" testId="tx-to">
          <Link to={`/address/${t.to_address}`} className="ks-link mono break-all">{t.to_address}</Link>
          <CopyBtn text={t.to_address} />
        </DetailRow>

        <SectionDivider label="Value & Fees" />

        <DetailRow label="Value" testId="tx-value">
          <span className="font-semibold">{formatEth(t.value_eth, 8)} ETH</span>
        </DetailRow>
        <DetailRow label="Transaction Fee" testId="tx-fee">{formatEth(t.fee_eth, 8)} ETH</DetailRow>
        <DetailRow label="Gas Price" testId="tx-gas-price">
          {effectiveGwei} Gwei <span className="text-[var(--ks-text-soft)] text-xs ml-2">({(effectiveGwei * 1e-9).toFixed(12)} ETH)</span>
        </DetailRow>

        <SectionDivider label="Gas Details" />

        <DetailRow label="Gas Limit & Usage by Txn" testId="tx-gas">
          <span>{formatNumber(t.gas)}</span>
          <span className="mx-2 text-[var(--ks-text-soft)]">|</span>
          <span>{formatNumber(t.gas_used)} ({gasUsedPct}%)</span>
        </DetailRow>
        <DetailRow label="Gas Fees" testId="tx-gas-fees">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span><span className="text-[var(--ks-text-soft)]">Base:</span> {baseFeeGwei} Gwei</span>
            <span><span className="text-[var(--ks-text-soft)]">Max:</span> {maxFeeGwei} Gwei</span>
            <span><span className="text-[var(--ks-text-soft)]">Max Priority:</span> {priorityGwei} Gwei</span>
          </div>
        </DetailRow>
        <DetailRow label="Burnt Fees" testId="tx-burnt">
          <span className="inline-flex items-center gap-1">
            <Flame size={14} className="text-orange-500" />
            <span className="font-mono">{burntEth.toFixed(12)} ETH</span>
          </span>
          {savingsEth > 0 && (
            <span className="ml-4 text-xs text-[var(--ks-text-soft)]">
              Txn Savings Fees: <span className="font-mono">{savingsEth.toFixed(12)} ETH</span>
            </span>
          )}
        </DetailRow>

        <SectionDivider label="More Details" />

        <div className="px-4 py-2">
          <button
            onClick={() => setShowMore((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-[var(--ks-primary)] hover:text-[var(--ks-link-hover)]"
            data-testid="toggle-more"
          >
            {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showMore ? "Hide" : "Click to see"} More
          </button>
        </div>

        {showMore && (
          <>
            <DetailRow label="Other Attributes" testId="tx-other-attrs">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="ks-badge">Txn Type: 2 (EIP-1559)</span>
                <span className="ks-badge">Nonce: {t.nonce}</span>
                <span className="ks-badge">Position in Block: {t.tx_index ?? 0}</span>
              </div>
            </DetailRow>
            <DetailRow label="Method" testId="tx-method"><MethodPill method={t.method} /></DetailRow>
            <DetailRow label="Input Data" testId="tx-input">
              <div className="bg-[var(--ks-surface)] border border-[var(--ks-border)] rounded p-3 font-mono text-xs break-all max-h-40 overflow-auto">
                {t.input_data || "0x"}
              </div>
            </DetailRow>
            <DetailRow label="Logs" testId="tx-logs">
              <span className="ks-badge">{t.logs_count} log entries</span>
              <span className="text-[var(--ks-text-soft)] text-xs ml-2">(full log decoding in Phase 2)</span>
            </DetailRow>
          </>
        )}
      </div>
    </Layout>
  );
}
